import React, { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import {
  collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, serverTimestamp
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { db, storage } from '../../firebase/firebase'
import { useAuth } from '../../contexts/AuthContext'
import AdminLayout from './AdminLayout'

// ── Markdown editor helpers ────────────────────────────────────────────────
const applyMarkdown = (el, action, onChange) => {
  if (!el) return null
  const start = el.selectionStart
  const end = el.selectionEnd
  const selected = el.value.substring(start, end)
  const before = el.value.substring(0, start)
  const after = el.value.substring(end)
  let newText = el.value, ns = start, ne = end

  if (action === 'bold') {
    const inner = selected || 'жирний текст'
    newText = before + `**${inner}**` + after
    ns = start + 2; ne = start + 2 + inner.length
  } else if (action === 'italic') {
    const inner = selected || 'курсив'
    newText = before + `*${inner}*` + after
    ns = start + 1; ne = start + 1 + inner.length
  } else if (action === 'strike') {
    const inner = selected || 'текст'
    newText = before + `~~${inner}~~` + after
    ns = start + 2; ne = start + 2 + inner.length
  } else if (action === 'link') {
    const url = window.prompt('URL посилання:', 'https://')
    if (!url) return null
    const inner = selected || 'текст посилання'
    newText = before + `[${inner}](${url})` + after
    ns = start + 1; ne = start + 1 + inner.length
  } else if (action === 'h2') {
    const lineStart = before.lastIndexOf('\n') + 1
    const lineText = before.substring(lineStart)
    if (lineText.startsWith('## ')) {
      newText = before.substring(0, lineStart) + lineText.substring(3) + selected + after
      ns = Math.max(lineStart, start - 3); ne = Math.max(lineStart, end - 3)
    } else {
      newText = before.substring(0, lineStart) + '## ' + lineText + selected + after
      ns = start + 3; ne = end + 3
    }
  } else if (action === 'list') {
    const inner = selected || 'пункт'
    const prefix = before.endsWith('\n') || before === '' ? '- ' : '\n- '
    newText = before + prefix + inner + after
    ns = start + prefix.length; ne = start + prefix.length + inner.length
  }

  onChange(newText)
  return { ns, ne }
}

const TOOLBAR_BTNS = [
  { id: 'bold',   label: <b>B</b>,   title: 'Жирний' },
  { id: 'italic', label: <i>I</i>,   title: 'Курсив' },
  { id: 'strike', label: <s>S</s>,   title: 'Закреслений' },
  null,
  { id: 'link',   label: '🔗',       title: 'Посилання' },
  null,
  { id: 'h2',     label: 'H2',       title: 'Підзаголовок' },
  { id: 'list',   label: '≡',        title: 'Маркований список' },
]

const MarkdownEditor = ({ value, onChange }) => {
  const [preview, setPreview] = useState(false)
  const ref = useRef(null)

  const apply = (action) => {
    const result = applyMarkdown(ref.current, action, onChange)
    if (result) setTimeout(() => { ref.current?.focus(); ref.current?.setSelectionRange(result.ns, result.ne) }, 0)
  }

  return (
    <div>
      <div style={s.mdToolbar}>
        {TOOLBAR_BTNS.map((btn, i) =>
          btn === null
            ? <div key={`sep-${i}`} style={s.mdSep} />
            : <button key={btn.id} type='button' onClick={() => apply(btn.id)} style={s.mdBtn} title={btn.title}>{btn.label}</button>
        )}
        <div style={{ flex: 1 }} />
        <button type='button' onClick={() => setPreview(p => !p)} style={{ ...s.mdBtn, fontSize: '12px', padding: '4px 10px' }}>
          {preview ? '✎ Редагувати' : '👁 Перегляд'}
        </button>
      </div>
      {preview ? (
        <div style={s.mdPreview} className='nf-md-content'>
          {value
            ? <ReactMarkdown>{value}</ReactMarkdown>
            : <span style={{ color: '#b8a090' }}>Текст порожній…</span>
          }
        </div>
      ) : (
        <textarea
          ref={ref}
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{ ...s.input, height: '180px', resize: 'vertical', borderRadius: '0 0 8px 8px' }}
          placeholder={'Текст новини…\n\nПідказка: **жирний**, *курсив*, [посилання](https://…)'}
        />
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
const emptyForm = { title: '', content: '', photoFile: null, photoUrl: '' }
const emptyFilters = { search: '', dateFrom: '', dateTo: '' }

const AdminPages = () => {
  const { currentUser } = useAuth()
  const [news, setNews] = useState([])
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [preview, setPreview] = useState(null)
  const [filters, setFilters] = useState(emptyFilters)
  const [selected, setSelected] = useState([])
  const [bulkDeleting, setBulkDeleting] = useState(false)

  useEffect(() => {
    return onSnapshot(collection(db, 'news'), snap =>
      setNews(
        snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      )
    )
  }, [])

  const filteredNews = news.filter(item => {
    if (filters.search && !item.title?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false
    }
    if (item.createdAt) {
      const itemDate = new Date(item.createdAt.seconds * 1000)
      if (filters.dateFrom) {
        const from = new Date(filters.dateFrom)
        from.setHours(0, 0, 0, 0)
        if (itemDate < from) return false
      }
      if (filters.dateTo) {
        const to = new Date(filters.dateTo)
        to.setHours(23, 59, 59, 999)
        if (itemDate > to) return false
      }
    } else if (filters.dateFrom || filters.dateTo) {
      return false
    }
    return true
  })

  const hasActiveFilters = filters.search || filters.dateFrom || filters.dateTo

  const allFilteredSelected = filteredNews.length > 0 && filteredNews.every(item => selected.includes(item.id))

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelected(prev => prev.filter(id => !filteredNews.some(item => item.id === id)))
    } else {
      setSelected(prev => [...new Set([...prev, ...filteredNews.map(item => item.id)])])
    }
  }

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const handleBulkDelete = async () => {
    if (selected.length === 0) return
    if (!window.confirm(`Видалити ${selected.length} новин(и)?`)) return
    setBulkDeleting(true)
    try {
      const items = news.filter(item => selected.includes(item.id))
      for (const item of items) {
        if (item.photoUrl) { try { await deleteObject(ref(storage, item.photoUrl)) } catch {} }
        await deleteDoc(doc(db, 'news', item.id))
      }
      setSelected([])
    } finally {
      setBulkDeleting(false)
    }
  }

  const handlePhotoChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setForm(f => ({ ...f, photoFile: file }))
    setPreview(URL.createObjectURL(file))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.title.trim()) return
    setSaving(true)
    try {
      let photoUrl = form.photoUrl
      if (form.photoFile) {
        const r = ref(storage, `news/${Date.now()}_${form.photoFile.name}`)
        await uploadBytes(r, form.photoFile)
        photoUrl = await getDownloadURL(r)
      }
      if (editingId) {
        await updateDoc(doc(db, 'news', editingId), {
          title: form.title.trim(), content: form.content.trim(), photoUrl, updatedAt: serverTimestamp(),
        })
      } else {
        await addDoc(collection(db, 'news'), {
          title: form.title.trim(), content: form.content.trim(), photoUrl,
          createdAt: serverTimestamp(), createdBy: currentUser?.uid || '',
        })
      }
      resetForm()
    } catch (err) {
      alert('Помилка: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (item) => {
    setEditingId(item.id)
    setForm({ title: item.title, content: item.content, photoFile: null, photoUrl: item.photoUrl || '' })
    setPreview(item.photoUrl || null)
    setShowForm(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async (item) => {
    if (!window.confirm('Видалити цю новину?')) return
    setDeleting(item.id)
    if (item.photoUrl) { try { await deleteObject(ref(storage, item.photoUrl)) } catch {} }
    await deleteDoc(doc(db, 'news', item.id))
    setDeleting(null)
  }

  const resetForm = () => {
    setForm(emptyForm); setEditingId(null); setShowForm(false); setPreview(null)
  }

  const formatDate = (ts) => {
    if (!ts) return ''
    return new Date(ts.seconds * 1000).toLocaleDateString('uk-UA', {
      day: 'numeric', month: 'long', year: 'numeric',
    })
  }

  return (
    <AdminLayout>
      <div style={s.page}>
        <div style={s.pageHeader}>
          <div>
            <h1 style={s.pageTitle}>Новини</h1>
            <p style={s.pageDesc}>Публікуйте та керуйте новинами сайту</p>
          </div>
          {!showForm && (
            <button onClick={() => setShowForm(true)} style={s.btnPrimary}>
              + Нова новина
            </button>
          )}
        </div>

        {showForm && (
          <div style={{ ...s.card, marginBottom: '28px' }}>
            <div style={s.cardHeader}>
              <span style={s.cardIcon}>✍️</span>
              <span style={s.cardTitle}>{editingId ? 'Редагувати новину' : 'Нова новина'}</span>
            </div>

            <form onSubmit={handleSave} style={s.formReset}>
              <div style={s.field}>
                <label style={s.label}>Заголовок</label>
                <input
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  style={s.input}
                  placeholder='Назва новини'
                  required
                />
              </div>

              <div style={s.field}>
                <label style={s.label}>Текст</label>
                <MarkdownEditor
                  value={form.content}
                  onChange={val => setForm(f => ({ ...f, content: val }))}
                />
              </div>

              <div style={s.field}>
                <label style={s.label}>Фото</label>
                {preview ? (
                  <div style={s.previewBox}>
                    <img src={preview} alt='preview' style={s.previewImg} />
                    <div style={s.previewMeta}>
                      <div style={s.previewName}>{form.photoFile?.name || 'Поточне фото'}</div>
                      <button
                        type='button'
                        onClick={() => { setPreview(null); setForm(f => ({ ...f, photoFile: null, photoUrl: '' })) }}
                        style={s.removeBtn}
                      >
                        ✕ Видалити фото
                      </button>
                    </div>
                  </div>
                ) : (
                  <label style={s.uploadZone}>
                    <input type='file' accept='image/*' onChange={handlePhotoChange} style={{ display: 'none' }} />
                    <span style={s.uploadIcon}>🖼</span>
                    <span style={s.uploadText}>Натисніть для завантаження фото</span>
                    <span style={s.uploadHint}>PNG, JPG до 10 МБ</span>
                  </label>
                )}
              </div>

              <div style={s.btnRow}>
                <button type='submit' disabled={saving} style={s.btnPrimary}>
                  {saving ? 'Збереження…' : editingId ? 'Зберегти зміни' : 'Опублікувати'}
                </button>
                <button type='button' onClick={resetForm} style={s.btnGhost}>
                  Скасувати
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Filters */}
        <div style={{ ...s.card, marginBottom: '20px' }}>
          <div style={s.filterRow}>
            <div style={{ ...s.field, flex: 2, marginBottom: 0 }}>
              <label style={s.label}>Пошук за назвою</label>
              <input
                value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                style={s.input}
                placeholder='Назва новини…'
              />
            </div>
            <div style={{ ...s.field, flex: 1, marginBottom: 0 }}>
              <label style={s.label}>Дата з</label>
              <input
                type='date'
                value={filters.dateFrom}
                onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                style={s.input}
              />
            </div>
            <div style={{ ...s.field, flex: 1, marginBottom: 0 }}>
              <label style={s.label}>Дата по</label>
              <input
                type='date'
                value={filters.dateTo}
                onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                style={s.input}
              />
            </div>
            {hasActiveFilters && (
              <button onClick={() => setFilters(emptyFilters)} style={{ ...s.btnGhost, flexShrink: 0 }}>
                Скинути
              </button>
            )}
          </div>
        </div>

        {/* News list */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <label style={s.selectAllLabel}>
              <input
                type='checkbox'
                checked={allFilteredSelected}
                onChange={toggleSelectAll}
                disabled={filteredNews.length === 0}
                style={s.checkboxNative}
              />
            </label>
            <span style={s.cardIcon}>📰</span>
            <span style={s.cardTitle}>
              {hasActiveFilters ? 'Знайдено' : 'Всі новини'}
            </span>
            <span style={s.badge}>{filteredNews.length}</span>

            {selected.length > 0 && (
              <button onClick={handleBulkDelete} disabled={bulkDeleting} style={s.btnBulkDelete}>
                🗑 Видалити вибрані ({selected.length})
              </button>
            )}
          </div>

          {filteredNews.length === 0 && (
            <div style={s.empty}>
              {hasActiveFilters ? 'Нічого не знайдено за заданими фільтрами.' : 'Новин ще немає. Опублікуйте першу новину.'}
            </div>
          )}

          <div style={s.newsList}>
            {filteredNews.map(item => (
              <div key={item.id} style={s.newsRow}>
                <input
                  type='checkbox'
                  checked={selected.includes(item.id)}
                  onChange={() => toggleSelect(item.id)}
                  style={{ ...s.checkboxNative, marginTop: '4px' }}
                />
                {item.photoUrl
                  ? <img src={item.photoUrl} alt={item.title} style={s.thumb} />
                  : <div style={s.thumbEmpty}>🖼</div>
                }
                <div style={s.newsBody}>
                  <div style={s.newsTitle}>{item.title}</div>
                  <div style={s.newsDate}>{formatDate(item.createdAt)}</div>
                  {item.content && (
                    <div style={s.newsExcerpt}>
                      {item.content.slice(0, 130)}{item.content.length > 130 ? '…' : ''}
                    </div>
                  )}
                </div>
                <div style={s.newsActions}>
                  <button onClick={() => handleEdit(item)} style={s.actionBtn}>✏️ Редагувати</button>
                  <button
                    onClick={() => handleDelete(item)}
                    disabled={deleting === item.id}
                    style={{ ...s.actionBtn, color: '#b84040', borderColor: '#f5d0d0' }}
                  >
                    🗑 Видалити
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

const s = {
  page: { maxWidth: '900px' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' },
  pageTitle: { margin: '0 0 6px', fontSize: '24px', fontWeight: '700', color: '#2d1f14' },
  pageDesc: { margin: 0, fontSize: '14px', color: '#9a7c63' },

  card: {
    background: '#fff',
    borderRadius: '14px',
    padding: '28px',
    boxShadow: '0 2px 16px rgba(92,61,46,0.07)',
    border: '1px solid #f0e4d4',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '22px',
    paddingBottom: '16px',
    borderBottom: '1px solid #f5ede3',
  },
  cardIcon: { fontSize: '18px' },
  cardTitle: { fontSize: '15px', fontWeight: '700', color: '#2d1f14', flex: 1 },
  badge: {
    background: '#f0e6d8',
    color: '#8b6044',
    padding: '2px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },

  field: { marginBottom: '20px' },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: '#8b6044',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    border: '1.5px solid #e8d8c8',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#2d1f14',
    background: '#fdfaf7',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
    resize: 'none',
  },

  mdToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    padding: '6px 8px',
    background: '#f5ede3',
    border: '1.5px solid #e8d8c8',
    borderBottom: 'none',
    borderRadius: '8px 8px 0 0',
  },
  mdBtn: {
    background: '#fff',
    border: '1px solid #e0cebb',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: 'inherit',
    padding: '4px 8px',
    color: '#6b4a32',
    lineHeight: '1.2',
    minWidth: '28px',
    textAlign: 'center',
  },
  mdSep: {
    width: '1px',
    height: '18px',
    background: '#d8c4b0',
    margin: '0 4px',
    flexShrink: 0,
  },
  mdPreview: {
    minHeight: '180px',
    padding: '11px 14px',
    border: '1.5px solid #e8d8c8',
    borderRadius: '0 0 8px 8px',
    background: '#fdfaf7',
    fontSize: '14px',
    color: '#2d1f14',
    lineHeight: '1.6',
  },
  formReset: {
    width: '100%', margin: 0, marginTop: 0, padding: 0,
    border: 'none', borderRadius: 0, background: 'none', boxShadow: 'none', display: 'block',
  },
  uploadZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '28px',
    border: '2px dashed #e0cebb',
    borderRadius: '10px',
    cursor: 'pointer',
    background: '#fdfaf7',
    transition: 'border-color 0.2s',
    gap: '6px',
  },
  uploadIcon: { fontSize: '28px', marginBottom: '4px' },
  uploadText: { fontSize: '14px', color: '#6b4a32', fontWeight: '600' },
  uploadHint: { fontSize: '12px', color: '#b8a090' },

  previewBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '14px',
    background: '#fdfaf7',
    border: '1.5px solid #e8d8c8',
    borderRadius: '10px',
  },
  previewImg: { width: '80px', height: '60px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 },
  previewMeta: { flex: 1 },
  previewName: { fontSize: '13px', color: '#2d1f14', fontWeight: '500', marginBottom: '6px' },
  removeBtn: {
    background: 'none',
    border: 'none',
    color: '#b84040',
    cursor: 'pointer',
    fontSize: '13px',
    padding: 0,
    fontFamily: 'inherit',
  },

  btnRow: { display: 'flex', gap: '10px', marginTop: '8px' },
  btnPrimary: {
    padding: '11px 24px',
    background: 'linear-gradient(135deg, #6b4a32, #8b6044)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnGhost: {
    padding: '11px 20px',
    background: 'transparent',
    color: '#8b6044',
    border: '1.5px solid #e8d8c8',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  filterRow: { display: 'flex', gap: '16px', alignItems: 'flex-end' },

  selectAllLabel: { display: 'flex', alignItems: 'center', cursor: 'pointer' },
  checkboxNative: {
    width: '17px',
    height: '17px',
    accentColor: '#8b6044',
    cursor: 'pointer',
    flexShrink: 0,
  },
  btnBulkDelete: {
    padding: '7px 16px',
    background: '#fdf2f2',
    color: '#b84040',
    border: '1.5px solid #f5c6c6',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },

  empty: { color: '#b8a090', fontSize: '14px', padding: '16px 0', textAlign: 'center' },

  newsList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  newsRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '16px',
    borderRadius: '10px',
    background: '#fdfaf7',
    border: '1px solid #f0e4d4',
  },
  thumb: { width: '86px', height: '64px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 },
  thumbEmpty: {
    width: '86px',
    height: '64px',
    borderRadius: '8px',
    background: '#f0e6d8',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    color: '#c4986a',
  },
  newsBody: { flex: 1, minWidth: 0 },
  newsTitle: { fontSize: '15px', fontWeight: '700', color: '#2d1f14', marginBottom: '4px' },
  newsDate: { fontSize: '11px', color: '#b8a090', marginBottom: '6px' },
  newsExcerpt: { fontSize: '13px', color: '#7a5c44', lineHeight: '1.5' },
  newsActions: { display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 },
  actionBtn: {
    background: '#fff',
    border: '1px solid #e8d8c8',
    borderRadius: '7px',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '7px 12px',
    color: '#6b4a32',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
}

export default AdminPages
